import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { onAuthStateChanged } from 'firebase/auth';
import { onSnapshot, doc } from 'firebase/firestore';
import { firebaseAuth, firestore } from './firebase';
import { signout } from './Actions/userActions';

import { BrowserRouter, Route, Switch } from 'react-router-dom';
import { SearchProvider } from './Context/SearchContext';
import { ToastProvider } from './Context/ToastContext';
import CartScreen from './Screens/CartScreen';
import HomeScreen from './Screens/HomeScreen';
import ProductScreens from './Screens/ProductScreens';
import ProductScreen from './Screens/ProductScreen';
import SignInScreen from './Screens/SignInScreen';
import Header from './Components/Header';
import Footer from './Components/Footer';
import RegisterScreen from './Screens/RegisterScreen';
import OTPVerificationScreen from './Screens/OTPVerificationScreen';
import PendingApprovalScreen from './Screens/PendingApprovalScreen';
// import ShippingAddressScreen from './Screens/ShippingAddressScreen';
import DeliveryScreen from './Screens/DeliveryScreen';
import PaymentMethodScreen from './Screens/PaymentMethodScreen';
import PlaceOrderScreen from './Screens/PlaceOrderScreen';
import PaymentScreen from './Screens/PaymentScreen';
import OrderSuccessScreen from './Screens/OrderSuccessScreen';
import Privacy from './Screens/Privacy';
import TermsOfUse from './Screens/TermsOfUse';
import HelpCenter from './Screens/HelpCenter';
import DataDeletion from './Screens/DataDeletion';
import ContactUs from './Screens/ContactUs';
import ReturnsRefunds from './Screens/ReturnsRefunds';
import About from './Screens/About';
import ResetPasswordScreen from './Screens/ResetPasswordScreen';
import ShippingPolicy from './Screens/ShippingPolicy';
import VerifyEmailScreen from './Screens/VerifyEmailScreen';
import ProfilePage from './Screens/ProfilePage';
import ProfileScreen from './Screens/ProfileScreen';
import OrdersScreen from './Screens/OrdersScreen';
import ReturnsScreen from './Screens/ReturnsScreen';
import RewardsScreen from './Screens/RewardsScreen';
import WishlistScreen from './Screens/WishlistScreen';
import NotificationsScreen from './Screens/NotificationsScreen';
import { startOrderStatusMonitoring, startOrderItemStatusMonitoring } from './utils/orderStatusMonitor';
import AppInstallBanner from './Components/AppInstallBanner';



function App() {   
  const dispatch = useDispatch();
  const userSignin = useSelector((state) => state.userSignin);
  const { userInfo } = userSignin || {};
  const [authInitializing, setAuthInitializing] = React.useState(true);
  // Keep cleanup in a ref to avoid triggering re-renders/loops
  const orderMonitorCleanupRef = React.useRef(null);

  // App-level guard: if Firebase auth is null while local user state exists, force signout
  React.useEffect(() => {
    const unsubscribe = onAuthStateChanged(firebaseAuth, (user) => {
      if (user) {
        // User is signed in
        if (!userInfo) {
          // Firebase user exists but Redux state is empty - restore user state
          dispatch({
            type: 'USER_SIGNIN_SUCCESS',
            payload: {
              uid: user.uid,
              email: user.email,
              displayName: user.displayName,
              emailVerified: user.emailVerified,
            },
          });
        }
      } else {
        // User is signed out
        if (userInfo) {
          // Firebase auth is null but Redux state exists - force signout
          dispatch({ type: 'USER_SIGNOUT' });
        }
      }
      setAuthInitializing(false);
    });

    return () => unsubscribe();
  }, [dispatch, userInfo]);

  // Start order status monitoring when user is logged in
  React.useEffect(() => {
    const userId = userInfo?.uid || userInfo?.id;

    // On auth change, first cleanup any existing listeners
    if (orderMonitorCleanupRef.current) {
      try { orderMonitorCleanupRef.current(); } catch (_) {}
      orderMonitorCleanupRef.current = null;
    }

    if (!userId) {
      return () => {};
    }

    // Start monitoring order status changes
    const cleanup1 = startOrderStatusMonitoring(userId);
    const cleanup2 = startOrderItemStatusMonitoring(userId);
    const combinedCleanup = () => {
      if (cleanup1) cleanup1();
      if (cleanup2) cleanup2();
    };
    orderMonitorCleanupRef.current = combinedCleanup;

    return combinedCleanup;
  }, [userInfo?.uid, userInfo?.id]);

  // Lightweight notifications listener (single document parity with Android)
  React.useEffect(() => {
    if (!userInfo?.uid) return;
    let unsubscribe = null;
    const lastSizeRef = { current: null };

    try {
      const notifDocRef = doc(firestore, 'USERS', userInfo.uid, 'USER_DATA', 'MY_NOTIFICATIONS');
      unsubscribe = onSnapshot(notifDocRef, (snap) => {
        const data = snap.data() || {};
        const size = Number(data.list_size) || 0;
        if (lastSizeRef.current === null) {
          // Initial load: just record size, no toasts
          lastSizeRef.current = size;
          return;
        }
        if (size > lastSizeRef.current) {
          // New notifications arrived; fire a generic toast
          try {
            const event = new CustomEvent('show-notification-toast', {
              detail: {
                title: 'New notifications',
                body: `${size - lastSizeRef.current} new notification(s)`,
                type: 'info'
              }
            });
            window.dispatchEvent(event);
          } catch (err) {
            console.warn('Failed to dispatch toast event:', err);
          }
        }
        lastSizeRef.current = size;
      }, (error) => {
        console.error('Notifications listener error (doc):', error);
      });
    } catch (error) {
      console.error('Failed to setup notifications listener (doc):', error);
    }

    return () => {
      if (unsubscribe) {
        try { unsubscribe(); } catch (err) { console.warn('Error unsubscribing from notifications:', err); }
      }
    };
  }, [userInfo?.uid]);

  return (
    <ToastProvider>
      <SearchProvider>
        <BrowserRouter>
            <div className="grid-container">
              <Header/>
              <main>
          {authInitializing ? (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '60vh' }}>
              <div className="loading-container">
                <div className="loading-spinner"></div>
                <div className="loading-text">Loading…</div>
              </div>
            </div>
          ) : (
            <Switch>
              <Route path="/cart/:id?" component= {CartScreen} />
              <Route path="/product/:id" component={ProductScreen} />

              <Route exact path="/signin" component={SignInScreen} />
              <Route exact path="/register" component={RegisterScreen} />
              <Route exact path="/otp-verification" component={OTPVerificationScreen} />
              <Route exact path="/pending-approval" component={PendingApprovalScreen} />
              {/* <Route path="/shipping" component={ShippingAddressScreen}></Route> */}
              <Route path="/shipping" component={DeliveryScreen} />
              {/* Important: put the parameterized route before or make /payment exact */}
              <Route path="/payment/:orderId" component={PaymentScreen} />
              <Route exact path="/payment" component={PaymentMethodScreen} />
              <Route exact path="/placeorder" component={PlaceOrderScreen} />
              <Route exact path="/order-success" component={OrderSuccessScreen} />
              <Route exact path="/reset-password" component={ResetPasswordScreen} />
              <Route exact path="/verify-email" component={VerifyEmailScreen} />
              <Route exact path="/privacy" component={Privacy} />
              <Route exact path="/terms" component={TermsOfUse} />
              <Route exact path="/help" component={HelpCenter} />
              <Route exact path="/data-deletion" component={DataDeletion} />
              <Route exact path="/contact" component={ContactUs} />
              <Route exact path="/returns-refunds" component={ReturnsRefunds} />
              <Route exact path="/shipping-policy" component={ShippingPolicy} />
              <Route exact path="/about" component={About} />
              <Route exact path="/profile" component={ProfileScreen} />
              <Route exact path="/orders" component={OrdersScreen} />
              <Route exact path="/returns" component={ReturnsScreen} />
              <Route exact path="/rewards" component={RewardsScreen} />
              <Route exact path="/wishlist" component={WishlistScreen} />
              <Route exact path="/notifications" component={NotificationsScreen} />
              <Route exact path="/" component={HomeScreen} />
            </Switch>
          )}
            
          </main>
          <Footer />
          <AppInstallBanner />
          </div>
        </BrowserRouter>
      </SearchProvider>
    </ToastProvider>
  );
}

export default App;